import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { useCreateI9Record, useCreateEVerifyCase } from "@/hooks/useWorkerI9";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { AlertTriangle, Shield, Loader2 } from "lucide-react";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vendorId: string;
  vendorName: string;
  workerId?: string;
  companyId: string;
}

const LIST_A_DOCS = [
  { value: "us_passport", label: "U.S. Passport" },
  { value: "passport_card", label: "Passport Card" },
  { value: "perm_resident_card", label: "Permanent Resident Card (I-551)" },
  { value: "employment_auth_card", label: "Employment Auth. Card (I-766)" },
  { value: "foreign_passport_i94", label: "Foreign Passport + I-94" },
];

const LIST_B_DOCS = [
  { value: "drivers_license", label: "Driver's License" },
  { value: "state_id", label: "State ID" },
  { value: "school_id", label: "School ID (with photo)" },
  { value: "other", label: "Other" },
];

const LIST_C_DOCS = [
  { value: "social_security_card", label: "Social Security Card" },
  { value: "birth_certificate", label: "Birth Certificate" },
  { value: "us_citizen_id", label: "U.S. Citizen ID Card" },
  { value: "other", label: "Other" },
];

const IMPORT_SOURCES = [
  { value: "paper_i9_on_file", label: "Paper I-9 on file" },
  { value: "previous_system", label: "Previous system" },
  { value: "collected_in_person", label: "Collected in person" },
  { value: "other", label: "Other" },
];

export const ManualI9ImportDialog = ({ open, onOpenChange, vendorId, vendorName, workerId, companyId }: Props) => {
  const { user } = useAuth();
  const createI9 = useCreateI9Record();
  const createEVerify = useCreateEVerifyCase();

  // Section 1
  const [legalFirst, setLegalFirst] = useState("");
  const [legalMiddle, setLegalMiddle] = useState("");
  const [legalLast, setLegalLast] = useState("");
  const [otherNames, setOtherNames] = useState("");
  const [dob, setDob] = useState("");
  const [ssn, setSsn] = useState("");
  const [citizenshipStatus, setCitizenshipStatus] = useState("us_citizen");
  const [alienRegNum, setAlienRegNum] = useState("");
  const [i94Num, setI94Num] = useState("");
  const [foreignPassport, setForeignPassport] = useState("");
  const [foreignCountry, setForeignCountry] = useState("");
  const [workAuthExpiry, setWorkAuthExpiry] = useState("");
  const [firstDayOfWork, setFirstDayOfWork] = useState("");

  // Section 2
  const [docListType, setDocListType] = useState("list_a");
  const [listAType, setListAType] = useState("");
  const [listANum, setListANum] = useState("");
  const [listAAuthority, setListAAuthority] = useState("");
  const [listAExpiry, setListAExpiry] = useState("");
  const [listBType, setListBType] = useState("");
  const [listBNum, setListBNum] = useState("");
  const [listBState, setListBState] = useState("");
  const [listBExpiry, setListBExpiry] = useState("");
  const [listCType, setListCType] = useState("");
  const [listCNum, setListCNum] = useState("");
  const [listCAuthority, setListCAuthority] = useState("");

  // Import metadata
  const [importSource, setImportSource] = useState("paper_i9_on_file");
  const [importNotes, setImportNotes] = useState("");

  // E-Verify
  const [includeEVerify, setIncludeEVerify] = useState(false);
  const [everifyCaseNum, setEverifyCaseNum] = useState("");
  const [everifyStatus, setEverifyStatus] = useState("authorized");
  const [isManualCase, setIsManualCase] = useState(true);

  const saving = createI9.isPending || createEVerify.isPending;

  const handleSave = async () => {
    if (!legalFirst.trim() || !legalLast.trim() || !dob) {
      toast.error("Legal name and date of birth are required");
      return;
    }

    try {
      const ssnLast4 = ssn.length >= 4 ? ssn.slice(-4) : ssn || null;

      const i9Data: Record<string, any> = {
        company_id: companyId,
        vendor_id: vendorId,
        worker_id: workerId || null,
        legal_first_name: legalFirst.trim(),
        legal_middle_name: legalMiddle.trim() || null,
        legal_last_name: legalLast.trim(),
        other_last_names: otherNames.trim() || null,
        date_of_birth: dob,
        ssn_last4: ssnLast4,
        ssn_encrypted: ssn || null,
        citizenship_status: citizenshipStatus,
        alien_registration_number: citizenshipStatus === "lawful_permanent_resident" ? alienRegNum || null : null,
        i94_number: citizenshipStatus === "alien_authorized_to_work" ? i94Num || null : null,
        foreign_passport_number: citizenshipStatus === "alien_authorized_to_work" ? foreignPassport || null : null,
        foreign_passport_country: citizenshipStatus === "alien_authorized_to_work" ? foreignCountry || null : null,
        work_auth_expiry_date: citizenshipStatus === "alien_authorized_to_work" && workAuthExpiry ? workAuthExpiry : null,
        first_day_of_work: firstDayOfWork || null,
        document_list_type: docListType,
        is_manual_import: true,
        manual_import_by: user?.id,
        manual_import_at: new Date().toISOString(),
        manual_import_source: importSource,
        manual_import_notes: importNotes.trim() || null,
        section2_verified_by: user?.id,
        section2_verified_by_name: user?.email || "Admin",
        section2_verified_at: new Date().toISOString(),
        i9_status: "complete",
      };

      // Document fields
      if (docListType === "list_a") {
        i9Data.list_a_document_type = listAType || null;
        i9Data.list_a_document_number = listANum || null;
        i9Data.list_a_issuing_authority = listAAuthority || null;
        i9Data.list_a_expiry_date = listAExpiry || null;
      } else {
        i9Data.list_b_document_type = listBType || null;
        i9Data.list_b_document_number = listBNum || null;
        i9Data.list_b_issuing_state = listBState || null;
        i9Data.list_b_expiry_date = listBExpiry || null;
        i9Data.list_c_document_type = listCType || null;
        i9Data.list_c_document_number = listCNum || null;
        i9Data.list_c_issuing_authority = listCAuthority || null;
      }

      const i9Record = await createI9.mutateAsync(i9Data);

      // E-Verify case
      if (includeEVerify && i9Record?.id) {
        await createEVerify.mutateAsync({
          company_id: companyId,
          vendor_id: vendorId,
          i9_record_id: i9Record.id,
          case_number: everifyCaseNum || null,
          case_status: everifyStatus,
          date_of_hire: firstDayOfWork || dob,
          is_manual_case: isManualCase,
          manual_case_notes: isManualCase ? "Recorded offline E-Verify result" : null,
          submitted_at: everifyCaseNum ? new Date().toISOString() : null,
          result_received_at: everifyStatus === "authorized" ? new Date().toISOString() : null,
        });
      }

      // Log to compliance audit
      await (supabase.from("compliance_audit_log") as any).insert({
        company_id: companyId,
        vendor_id: vendorId,
        changed_by: user?.id,
        change_source: "admin",
        table_name: "worker_i9_records",
        field_name: "i9_manual_import",
        new_value: `${legalFirst} ${legalLast} — I-9 imported`,
        change_reason: `${IMPORT_SOURCES.find(s => s.value === importSource)?.label || importSource} — imported by admin`,
      });

      toast.success("I-9 record saved successfully");
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err.message || "Failed to save I-9 record");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Manual I-9 Import — {vendorName}
          </DialogTitle>
        </DialogHeader>

        <div className="rounded-md bg-muted/50 border p-3 text-sm text-muted-foreground">
          <p>ⓘ You are entering this information on behalf of the worker. This will be logged with your name and today's date for audit purposes.</p>
        </div>

        {/* SECTION 1 */}
        <div className="space-y-4">
          <h3 className="font-semibold text-sm">SECTION 1 — Worker Information</h3>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Legal First Name *</Label><Input value={legalFirst} onChange={e => setLegalFirst(e.target.value)} /></div>
            <div><Label>Middle Name</Label><Input value={legalMiddle} onChange={e => setLegalMiddle(e.target.value)} /></div>
            <div><Label>Legal Last Name *</Label><Input value={legalLast} onChange={e => setLegalLast(e.target.value)} /></div>
            <div><Label>Other Last Names</Label><Input value={otherNames} onChange={e => setOtherNames(e.target.value)} placeholder="Maiden, etc." /></div>
            <div><Label>Date of Birth *</Label><Input type="date" value={dob} onChange={e => setDob(e.target.value)} /></div>
            <div><Label>SSN (encrypted)</Label><Input type="password" value={ssn} onChange={e => setSsn(e.target.value)} placeholder="XXX-XX-XXXX" /></div>
          </div>

          <div>
            <Label>Citizenship Status</Label>
            <RadioGroup value={citizenshipStatus} onValueChange={setCitizenshipStatus} className="mt-2 space-y-2">
              <div className="flex items-center gap-2"><RadioGroupItem value="us_citizen" id="us_citizen" /><Label htmlFor="us_citizen" className="font-normal">U.S. Citizen</Label></div>
              <div className="flex items-center gap-2"><RadioGroupItem value="us_national" id="us_national" /><Label htmlFor="us_national" className="font-normal">U.S. National</Label></div>
              <div className="flex items-center gap-2"><RadioGroupItem value="lawful_permanent_resident" id="lpr" /><Label htmlFor="lpr" className="font-normal">Lawful Permanent Resident</Label></div>
              <div className="flex items-center gap-2"><RadioGroupItem value="alien_authorized_to_work" id="alien" /><Label htmlFor="alien" className="font-normal">Alien Authorized to Work</Label></div>
            </RadioGroup>
          </div>

          {citizenshipStatus === "lawful_permanent_resident" && (
            <div><Label>Alien Registration #</Label><Input value={alienRegNum} onChange={e => setAlienRegNum(e.target.value)} /></div>
          )}

          {citizenshipStatus === "alien_authorized_to_work" && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Work Auth Expiry</Label><Input type="date" value={workAuthExpiry} onChange={e => setWorkAuthExpiry(e.target.value)} /></div>
              <div><Label>I-94 #</Label><Input value={i94Num} onChange={e => setI94Num(e.target.value)} /></div>
              <div><Label>Foreign Passport #</Label><Input value={foreignPassport} onChange={e => setForeignPassport(e.target.value)} /></div>
              <div><Label>Country</Label><Input value={foreignCountry} onChange={e => setForeignCountry(e.target.value)} /></div>
            </div>
          )}

          <div><Label>First Day of Work</Label><Input type="date" value={firstDayOfWork} onChange={e => setFirstDayOfWork(e.target.value)} /></div>
        </div>

        <Separator />

        {/* SECTION 2 */}
        <div className="space-y-4">
          <h3 className="font-semibold text-sm">SECTION 2 — Document Verification</h3>
          <RadioGroup value={docListType} onValueChange={setDocListType} className="flex gap-4">
            <div className="flex items-center gap-2"><RadioGroupItem value="list_a" id="list_a" /><Label htmlFor="list_a" className="font-normal">List A (one document — identity + work auth)</Label></div>
            <div className="flex items-center gap-2"><RadioGroupItem value="list_b_c" id="list_b_c" /><Label htmlFor="list_b_c" className="font-normal">List B + List C (two documents)</Label></div>
          </RadioGroup>

          {docListType === "list_a" ? (
            <div className="space-y-3 rounded-md border p-3">
              <div><Label>Document Type</Label>
                <Select value={listAType} onValueChange={setListAType}>
                  <SelectTrigger><SelectValue placeholder="Select document" /></SelectTrigger>
                  <SelectContent>{LIST_A_DOCS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Document #</Label><Input value={listANum} onChange={e => setListANum(e.target.value)} /></div>
                <div><Label>Issued By</Label><Input value={listAAuthority} onChange={e => setListAAuthority(e.target.value)} /></div>
                <div><Label>Expiry Date</Label><Input type="date" value={listAExpiry} onChange={e => setListAExpiry(e.target.value)} /></div>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-3 rounded-md border p-3">
                <Badge variant="outline" className="text-[10px]">LIST B — Identity</Badge>
                <div><Label>Document Type</Label>
                  <Select value={listBType} onValueChange={setListBType}>
                    <SelectTrigger><SelectValue placeholder="Select document" /></SelectTrigger>
                    <SelectContent>{LIST_B_DOCS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Document #</Label><Input value={listBNum} onChange={e => setListBNum(e.target.value)} /></div>
                  <div><Label>Issuing State</Label><Input value={listBState} onChange={e => setListBState(e.target.value)} maxLength={2} /></div>
                  <div><Label>Expiry Date</Label><Input type="date" value={listBExpiry} onChange={e => setListBExpiry(e.target.value)} /></div>
                </div>
              </div>
              <div className="space-y-3 rounded-md border p-3">
                <Badge variant="outline" className="text-[10px]">LIST C — Work Authorization</Badge>
                <div><Label>Document Type</Label>
                  <Select value={listCType} onValueChange={setListCType}>
                    <SelectTrigger><SelectValue placeholder="Select document" /></SelectTrigger>
                    <SelectContent>{LIST_C_DOCS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Document #</Label><Input value={listCNum} onChange={e => setListCNum(e.target.value)} /></div>
                  <div><Label>Issued By</Label><Input value={listCAuthority} onChange={e => setListCAuthority(e.target.value)} /></div>
                </div>
              </div>
            </>
          )}
        </div>

        <Separator />

        {/* IMPORT SOURCE */}
        <div className="space-y-3">
          <h3 className="font-semibold text-sm">Import Source</h3>
          <Select value={importSource} onValueChange={setImportSource}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{IMPORT_SOURCES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
          <Textarea value={importNotes} onChange={e => setImportNotes(e.target.value)} placeholder="Notes about this import…" rows={2} />
        </div>

        <div className="rounded-md bg-warning/10 border border-warning/30 p-3 text-sm flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-warning mt-0.5 shrink-0" />
          <p>By saving, you certify you have physically examined the documents listed above and they appear genuine and relate to this worker. This is logged permanently.</p>
        </div>

        <Separator />

        {/* E-VERIFY */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Checkbox id="include_everify" checked={includeEVerify} onCheckedChange={(v) => setIncludeEVerify(!!v)} />
            <Label htmlFor="include_everify">Record E-Verify result</Label>
          </div>
          {includeEVerify && (
            <div className="grid grid-cols-2 gap-3 pl-6">
              <div><Label>Case #</Label><Input value={everifyCaseNum} onChange={e => setEverifyCaseNum(e.target.value)} placeholder="If already submitted" /></div>
              <div><Label>Status</Label>
                <Select value={everifyStatus} onValueChange={setEverifyStatus}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="authorized">Authorized</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="tentative_nonconfirmation">Tentative Non-Confirmation</SelectItem>
                    <SelectItem value="not_submitted">Not Yet Submitted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            Save & Create I-9 Record
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
